import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  clearStoredChallengeSession,
  deleteChallengeDraft,
  downloadMyChallengePhoto,
  fetchChallengeInfo,
  fetchMyChallengeStatuses,
  loadMyChallengeSubmission,
  readCachedChallengeInfo,
  readChallengeDraft,
  readStoredChallengeSession,
  submitChallenge,
  uploadChallengePhoto,
  verifyChallengeMember,
  writeChallengeDraft,
  type PublicChallengeInfo,
  type PublicChallengeItem,
  type PublicChallengeSession,
  type PublicChallengeStatus,
} from '../services/assignmentChallengePublicService';
import {
  CHALLENGE_TEXT_MAX,
  challengeThemeStyle,
  missionNeedsPhoto,
  missionNeedsText,
  normalizeChallengeIntro,
  type ChallengeAnswer,
} from '../types/assignmentChallenge';
import { compressChallengePhoto } from '../utils/challengePhotoCompress';
import {
  ChallengeListCard,
  ChallengeLogoBar,
  ChallengeProjectHead,
  ChallengeMissionScreen,
  ChallengeSuccessCard,
  type ChallengeCardStatus,
  type ChallengeLiveAnswer,
} from '../components/assignmentChallenge/ChallengeStage';

type Screen = 'entry' | 'list' | 'form';

type FormAnswer = {
  text: string;
  photoPath: string | null;
  /** 아직 서버 경로가 없거나(임시저장 복원) 미리보기용 원본 */
  photoBlob: Blob | null;
  photoUrl: string | null;
  uploading: boolean;
  error?: string;
};

const SESSION_EXPIRED_RE = /입장 시간이 지났어요/;

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function emptyAnswer(): FormAnswer {
  return { text: '', photoPath: null, photoBlob: null, photoUrl: null, uploading: false };
}

const ChallengePublicPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [info, setInfo] = useState<PublicChallengeInfo | null>(() => (token ? readCachedChallengeInfo(token) : null));
  const [infoError, setInfoError] = useState('');
  const [session, setSession] = useState<PublicChallengeSession | null>(() =>
    token ? readStoredChallengeSession(token) : null
  );
  const [statuses, setStatuses] = useState<PublicChallengeStatus[]>([]);
  const [screen, setScreen] = useState<Screen>(() => (token && readStoredChallengeSession(token) ? 'list' : 'entry'));
  const [activeId, setActiveId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [entryNumber, setEntryNumber] = useState('');
  const [entryBusy, setEntryBusy] = useState(false);
  const [entryError, setEntryError] = useState('');

  const [answers, setAnswers] = useState<Record<string, FormAnswer>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [submittedAt, setSubmittedAt] = useState('');
  const [formError, setFormError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  const requestIdRef = useRef<string | null>(null);
  const userEditedRef = useRef(false);
  const objectUrlsRef = useRef<string[]>([]);

  const trackUrl = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    objectUrlsRef.current.push(url);
    return url;
  };

  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    },
    []
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetchChallengeInfo(token)
      .then((next) => {
        if (!cancelled) {
          setInfo(next);
          setInfoError('');
        }
      })
      .catch((e) => {
        if (!cancelled) setInfoError(e instanceof Error ? e.message : '챌린지를 불러오지 못했어요.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (info?.projectTitle) document.title = `${info.projectTitle} · 챌린지`;
  }, [info?.projectTitle]);

  const handleSessionError = useCallback(
    (e: unknown): string => {
      const msg = e instanceof Error ? e.message : '요청에 실패했습니다.';
      if (SESSION_EXPIRED_RE.test(msg)) {
        clearStoredChallengeSession(token);
        setSessionExpired(true);
      }
      return msg;
    },
    [token]
  );

  const refreshStatuses = useCallback(
    async (s: PublicChallengeSession) => {
      try {
        setStatuses(await fetchMyChallengeStatuses(s.session));
      } catch (e) {
        const msg = handleSessionError(e);
        if (SESSION_EXPIRED_RE.test(msg)) {
          setSession(null);
          setScreen('entry');
          setEntryError(msg);
        }
      }
    },
    [handleSessionError]
  );

  useEffect(() => {
    if (session) void refreshStatuses(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session]);

  const statusById = useMemo(() => new Map(statuses.map((s) => [s.itemId, s])), [statuses]);
  const activeItem: PublicChallengeItem | null = useMemo(
    () => info?.items.find((i) => i.id === activeId) ?? null,
    [info, activeId]
  );
  const cardStatus = (id: string): ChallengeCardStatus => {
    const st = statusById.get(id);
    if (!st) return 'todo';
    return st.locked ? 'locked' : 'done';
  };
  const doneCount = info ? info.items.filter((i) => statusById.has(i.id)).length : 0;
  const firstItem = info?.items[0] ?? null;

  const enter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (entryBusy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setEntryError('이름을 입력해 주세요.');
      return;
    }
    if (!/^\d{6}$/.test(entryNumber)) {
      setEntryError('입장번호 6자리를 입력해 주세요.');
      return;
    }
    setEntryBusy(true);
    setEntryError('');
    try {
      const res = await verifyChallengeMember({ token, name: trimmed, entryNumber });
      setSession(res.session);
      setStatuses(res.statuses);
      setSessionExpired(false);
      setEntryNumber('');
      if (activeId && sessionExpired) {
        setScreen('form');
        void openForm(activeId, res.session);
      } else {
        setScreen('list');
      }
    } catch (err) {
      setEntryError(err instanceof Error ? err.message : '입장에 실패했어요.');
    } finally {
      setEntryBusy(false);
    }
  };

  const leave = () => {
    clearStoredChallengeSession(token);
    setSession(null);
    setStatuses([]);
    setActiveId(null);
    setAnswers({});
    setScreen('entry');
  };

  const openForm = async (itemId: string, s: PublicChallengeSession | null = session) => {
    const item = info?.items.find((i) => i.id === itemId);
    if (!item || !s) return;
    setFormLoading(true);
    setFormError('');
    setDraftNotice('');
    setLocked(false);
    setSubmittedAt('');
    userEditedRef.current = false;
    requestIdRef.current = null;
    const base: Record<string, FormAnswer> = {};
    item.content.missions.forEach((m) => {
      base[m.id] = emptyAnswer();
    });
    setAnswers(base);
    try {
      const existing = await loadMyChallengeSubmission(s.session, itemId);
      const next = { ...base };
      let isLocked = false;
      if (existing) {
        isLocked = existing.locked;
        setLocked(existing.locked);
        setSubmittedAt(existing.submittedAt);
        for (const a of existing.answers) {
          if (!next[a.missionId]) continue;
          next[a.missionId] = { ...next[a.missionId], text: a.text, photoPath: a.photoPath };
        }
        const withPhotos = existing.answers.filter((a) => a.photoPath && next[a.missionId]);
        const blobs = await Promise.all(
          withPhotos.map((a) => downloadMyChallengePhoto(a.photoPath as string).catch(() => null))
        );
        withPhotos.forEach((a, i) => {
          const blob = blobs[i];
          if (blob) next[a.missionId] = { ...next[a.missionId], photoUrl: trackUrl(blob) };
        });
      }
      if (!isLocked) {
        const draft = await readChallengeDraft(token, s.memberName, itemId);
        const draftNewer =
          draft &&
          draft.itemRevision === item.revision &&
          (!existing || Date.parse(draft.savedAt) > Date.parse(existing.submittedAt));
        if (draft && draftNewer) {
          Object.entries(draft.answers).forEach(([missionId, d]) => {
            if (!next[missionId]) return;
            const blob = d.photo ?? null;
            next[missionId] = {
              ...next[missionId],
              text: d.text ?? '',
              photoBlob: blob,
              photoPath: blob ? null : next[missionId].photoPath,
              photoUrl: blob ? trackUrl(blob) : next[missionId].photoUrl,
            };
          });
          setDraftNotice('이 기기에 임시 저장된 내용을 불러왔어요.');
        }
      }
      setAnswers(next);
    } catch (e) {
      setFormError(handleSessionError(e));
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    if (screen !== 'form' || !activeItem || !session || locked || formLoading) return undefined;
    if (!userEditedRef.current) return undefined;
    const t = window.setTimeout(() => {
      const payload: Record<string, { text: string; photo?: Blob | null; photoPath?: string | null }> = {};
      Object.entries(answers).forEach(([id, a]) => {
        payload[id] = { text: a.text, photo: a.photoBlob, photoPath: a.photoPath };
      });
      void writeChallengeDraft(token, session.memberName, activeItem.id, {
        itemRevision: activeItem.revision,
        answers: payload,
      });
    }, 700);
    return () => window.clearTimeout(t);
  }, [answers, screen, activeItem, session, locked, formLoading, token]);

  const patchAnswer = (missionId: string, patch: Partial<FormAnswer>) => {
    setAnswers((prev) => ({ ...prev, [missionId]: { ...(prev[missionId] ?? emptyAnswer()), ...patch } }));
  };

  const onText = (missionId: string, v: string) => {
    userEditedRef.current = true;
    patchAnswer(missionId, { text: v.slice(0, CHALLENGE_TEXT_MAX) });
  };

  const onPhoto = async (missionId: string, file: File) => {
    if (!activeItem || !session) return;
    if (!/^image\//.test(file.type) && !/\.(heic|heif)$/i.test(file.name)) {
      patchAnswer(missionId, { error: '이미지 파일만 올릴 수 있어요.' });
      return;
    }
    userEditedRef.current = true;
    patchAnswer(missionId, { uploading: true, error: undefined });
    let blob: Blob;
    try {
      blob = await compressChallengePhoto(file);
    } catch {
      patchAnswer(missionId, { uploading: false, error: '사진을 읽지 못했어요. 다른 사진을 골라 주세요.' });
      return;
    }
    const url = trackUrl(blob);
    patchAnswer(missionId, { photoBlob: blob, photoUrl: url, photoPath: null });
    try {
      const path = await uploadChallengePhoto({ session: session.session, itemId: activeItem.id, missionId, blob });
      patchAnswer(missionId, { photoPath: path, uploading: false });
    } catch (e) {
      const msg = handleSessionError(e);
      patchAnswer(missionId, {
        uploading: false,
        error: SESSION_EXPIRED_RE.test(msg) ? msg : `${msg} 제출할 때 다시 올려 볼게요.`,
      });
    }
  };

  const onRemovePhoto = (missionId: string) => {
    userEditedRef.current = true;
    patchAnswer(missionId, { photoBlob: null, photoUrl: null, photoPath: null, error: undefined });
  };

  const missions = activeItem?.content.missions ?? [];
  const anyUploading = Object.values(answers).some((a) => a.uploading);
  const canSubmit =
    !!activeItem &&
    !locked &&
    !formLoading &&
    !anyUploading &&
    missions.length > 0 &&
    missions.every((m) => {
      const a = answers[m.id];
      if (!a) return false;
      if (missionNeedsPhoto(m.submissionMode) && !a.photoPath && !a.photoBlob) return false;
      if (missionNeedsText(m.submissionMode) && !a.text.trim()) return false;
      return true;
    });

  const submit = async () => {
    if (!activeItem || !session || !canSubmit || submitting) return;
    setSubmitting(true);
    setFormError('');
    try {
      const resolved: Record<string, string | null> = {};
      for (const m of missions) {
        const a = answers[m.id];
        if (!missionNeedsPhoto(m.submissionMode)) {
          resolved[m.id] = null;
          continue;
        }
        if (a.photoPath) {
          resolved[m.id] = a.photoPath;
          continue;
        }
        if (!a.photoBlob) throw new Error('사진이 필요한 인증 항목이 있어요.');
        patchAnswer(m.id, { uploading: true, error: undefined });
        const path = await uploadChallengePhoto({
          session: session.session,
          itemId: activeItem.id,
          missionId: m.id,
          blob: a.photoBlob,
        });
        patchAnswer(m.id, { uploading: false, photoPath: path });
        resolved[m.id] = path;
        requestIdRef.current = null;
      }
      const payload: ChallengeAnswer[] = missions.map((m) => ({
        missionId: m.id,
        text: missionNeedsText(m.submissionMode) ? answers[m.id].text.trim() : '',
        photoPath: resolved[m.id] ?? null,
      }));
      requestIdRef.current = requestIdRef.current ?? newRequestId();
      const res = await submitChallenge({
        session: session.session,
        itemId: activeItem.id,
        requestId: requestIdRef.current,
        answers: payload,
      });
      requestIdRef.current = null;
      userEditedRef.current = false;
      setSubmittedAt(res.submittedAt);
      setDraftNotice('');
      await deleteChallengeDraft(token, session.memberName, activeItem.id);
      setSuccessOpen(true);
      void refreshStatuses(session);
    } catch (e) {
      setAnswers((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (next[k].uploading) next[k] = { ...next[k], uploading: false };
        });
        return next;
      });
      setFormError(handleSessionError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const liveAnswers = useMemo(() => {
    const out: Record<string, ChallengeLiveAnswer> = {};
    Object.entries(answers).forEach(([id, a]) => {
      out[id] = { text: a.text, photoUrl: a.photoUrl, uploading: a.uploading, error: a.error };
    });
    return out;
  }, [answers]);

  const goList = () => {
    setActiveId(null);
    setScreen('list');
    setFormError('');
  };

  if (!token) {
    return (
      <div className="cp-chl-public">
        <p className="cp-chl-public-error">잘못된 링크예요.</p>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="cp-chl-public">
        <div className="cp-chl-public-card">
          {infoError ? (
            <p className="cp-chl-public-error" role="alert">
              {infoError}
            </p>
          ) : (
            <p className="cp-chl-public-loading">챌린지를 불러오는 중…</p>
          )}
        </div>
      </div>
    );
  }

  const intro = info.intro ?? normalizeChallengeIntro(null, firstItem?.content);

  return (
    <div className="cp-chl-public" style={challengeThemeStyle(info.themeColor)}>
      <div className="cp-chl-app cp-chl-public-app">
        {screen === 'entry' ? (
          <section className="cp-chl-screen cp-chl-entry">
            <ChallengeLogoBar text={intro.topLogoText} position="top" />
            <p className="cp-chl-kicker">챌린지 참여</p>
            <h1 className="cp-chl-entry-title">{info.projectTitle || '챌린지'}</h1>
            <p className="cp-chl-entry-desc">선생님이 알려 준 이름과 입장번호 6자리로 들어와 주세요.</p>
            {infoError ? (
              <p className="cp-chl-public-error" role="alert">
                {infoError}
              </p>
            ) : null}
            <form className="cp-chl-entry-form" onSubmit={(e) => void enter(e)}>
              <label className="cp-chl-field">
                <span>이름</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  maxLength={40}
                  placeholder="이름"
                />
              </label>
              <label className="cp-chl-field">
                <span>입장번호</span>
                <input
                  value={entryNumber}
                  onChange={(e) => setEntryNumber(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={6}
                  placeholder="6자리 숫자"
                  type="password"
                />
              </label>
              {entryError ? (
                <p className="cp-chl-public-error" role="alert">
                  {entryError}
                </p>
              ) : null}
              <button type="submit" className="cp-chl-primary-btn" disabled={entryBusy}>
                {entryBusy ? '확인하는 중…' : '입장하기'}
              </button>
            </form>
            <ChallengeLogoBar text={intro.bottomLogoText} position="bottom" />
          </section>
        ) : null}

        {screen === 'list' && session ? (
          <section className="cp-chl-screen cp-chl-list">
            <ChallengeLogoBar text={intro.topLogoText} position="top" />
            <ChallengeProjectHead
              memberName={session.memberName}
              title={info.projectTitle}
              intro={intro}
              done={doneCount}
              total={info.items.length}
              onLeave={leave}
            />
            {info.items.length === 0 ? <p className="cp-chl-public-loading">아직 열린 챌린지가 없어요.</p> : null}
            <div className="cp-chl-card-list">
              {info.items.map((it) => (
                <ChallengeListCard
                  key={it.id}
                  item={it}
                  status={cardStatus(it.id)}
                  onClick={() => {
                    setActiveId(it.id);
                    setScreen('form');
                    void openForm(it.id);
                  }}
                />
              ))}
            </div>
            <ChallengeLogoBar text={intro.bottomLogoText} position="bottom" />
          </section>
        ) : null}

        {screen === 'form' && activeItem && session ? (
          <>
            <ChallengeLogoBar text={activeItem.content.topLogoText} position="top" />
            {formLoading ? (
              <p className="cp-chl-public-loading">불러오는 중…</p>
            ) : (
              <ChallengeMissionScreen
                item={activeItem}
                onBack={goList}
                live={{
                  answers: liveAnswers,
                  onText,
                  onPhoto: (id, file) => void onPhoto(id, file),
                  onRemovePhoto,
                  readOnly: locked,
                  submitting,
                  canSubmit,
                  submitLabel: submittedAt && !locked ? '수정해서 다시 제출' : undefined,
                  onSubmit: () => void submit(),
                  participant: (
                    <div className="cp-chl-participant">
                      <b className="cp-chl-participant-name">{session.memberName}</b>
                      {submittedAt ? (
                        <span className="cp-chl-participant-status">
                          {locked
                            ? '선생님이 확인했어요. 내용은 볼 수만 있어요.'
                            : '제출했어요. 선생님 확인 전까지 고칠 수 있어요.'}
                        </span>
                      ) : null}
                    </div>
                  ),
                  notice: (
                    <div className="cp-chl-form-notice">
                      {draftNotice ? <p>{draftNotice}</p> : null}
                      {!locked ? <p className="cp-chl-form-notice-sub">작성 중인 내용은 이 기기에만 임시 저장돼요.</p> : null}
                      {formError ? (
                        <p className="cp-chl-public-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                      {sessionExpired ? (
                        <button
                          type="button"
                          className="cp-chl-ghost-btn"
                          onClick={() => {
                            setSession(null);
                            setScreen('entry');
                          }}
                        >
                          다시 입장하기
                        </button>
                      ) : null}
                    </div>
                  ),
                }}
              />
            )}
            <ChallengeLogoBar text={activeItem.content.bottomLogoText} position="bottom" />
          </>
        ) : null}
      </div>

      {successOpen && activeItem ? (
        <div className="cp-chl-success-backdrop" role="dialog" aria-modal="true" aria-label="제출 완료">
          <ChallengeSuccessCard
            content={activeItem.content}
            onClose={() => {
              setSuccessOpen(false);
              goList();
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ChallengePublicPage;
